import moment from "moment/moment";

export function getCurrentDateTimeISO() {
  return moment().format('YYYY-MM-DDTHH:mm:ss.SSS');
}

export function formatDate(date, format = 'YYYY-MM-DD') {
  return moment(date).format(format);
}

export function formatDateLocale(
  dateInput,
  {
    locale = navigator.language,
    year = 'numeric',
    month = '2-digit',
    day = '2-digit',
    weekday, // optional: e.g., 'short' or 'long'
    fallback = '',
  } = {}
) {
  if (!dateInput) return fallback;

  const date = typeof dateInput === 'string' || typeof dateInput === 'number'
    ? new Date(dateInput)
    : dateInput instanceof Date
      ? dateInput
      : null;

  if (!date || isNaN(date.getTime())) return fallback;

  try {
    return new Intl.DateTimeFormat(locale, {
      year,
      month,
      day,
      ...(weekday ? { weekday } : {}),
    }).format(date);
  } catch {
    return fallback;
  }
}

export function getCurrentWeekdays(date, days = 5) {
  const today = new Date(date);
  const dayOfWeek = today.getDay(); // 0 (Sunday) to 6 (Saturday)

  // Calculate Monday of the current week
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

  // Generate weekdays according days parameter
  const weekdays = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    weekdays.push(formatDate(date));
  }

  return weekdays;
}

export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function formatFullDate(date, locale = navigator.language, options = {}) {
  const defaultOptions = {
    weekday: 'short',  // 'short', 'long', 'narrow'
    day: '2-digit',    // 'numeric', '2-digit'
    month: '2-digit',  // 'numeric', '2-digit', 'long', 'short', 'narrow'
    year: '2-digit'    // 'numeric', '2-digit'
  };

  // Merge custom options with default ones
  const mergedOptions = { ...defaultOptions, ...options };

  if (typeof date === "string") {
    date = new Date(date);
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.warn("Invalid date passed to formatFullDate:", date);
    return "Invalid Date";
  }

  let formatted = new Intl.DateTimeFormat(locale, mergedOptions).format(date);
  // Remove trailing dot from weekday if present (e.g., 'Mon.' -> 'Mon')
  formatted = formatted.replace(/^(\p{L}{2,4})\./u, '$1');
  return formatted;
}

/**
 * Adds hours, minutes, and seconds (e.g., "13:00:00") to a given date.
 * @param {Date|string} date - The base date to which the time will be added. Can be a Date object or a string.
 * @param {string} time - The time in the format "HH:mm:ss" or "HH:mm". Seconds are optional.
 * @returns {Date} - The updated date with the added time. Returns null if the input is invalid.
 */
export function addTimeToDate(date, time) {
  if (typeof date === "string") {
    date = new Date(date);
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.warn("Invalid date passed to addTimeToDate:", date);
    return null;
  }

  const [hours, minutes, seconds = 0] = time.split(":").map(Number);

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
    console.warn("Invalid time format passed to addTimeToDate:", time);
    return null;
  }

  const updatedDate = new Date(date);
  updatedDate.setHours(hours, minutes, seconds, 0);
  return updatedDate;
}
